import React, { useEffect, useCallback } from "react";
import {
    FocusContext,
    useFocusable,
    setFocus,
    init,
} from "@noriginmedia/norigin-spatial-navigation";
import { forceAppUpdate } from "../../utils/pwaManager";
import { useInputMode } from "../../utils/hooks/useInputMode";

/**
 * Force Update Modal
 * 
 * Un-dismissable modal that appears when app version is below minimum required.
 * Focus is trapped on the Update button - no escape, no backdrop dismiss.
 * Uses legacy CSS for Smart TV compatibility (LG WebOS / Samsung Tizen).
 */

// Ensure spatial navigation is initialized
init({
    debug: false,
    visualDebug: false,
});

// Update button with focus handling
function UpdateButton({ onUpdate, isUpdating }) {
    var { ref, focused, focusSelf } = useFocusable({
        focusKey: "force-update-button",
        onEnterPress: function () {
            if (!isUpdating) {
                onUpdate();
            }
        },
    });

    var { setMouseFocus } = useInputMode();
    var handleMouseEnter = useCallback(function () {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    // Auto-focus on mount
    useEffect(function () {
        var timer = setTimeout(function () {
            focusSelf();
        }, 100);
        return function () { clearTimeout(timer); };
    }, [focusSelf]);

    // Keep focus trapped - re-focus if somehow lost
    useEffect(function () {
        if (!focused) {
            var timer = setTimeout(function () {
                setFocus("force-update-button");
            }, 200);
            return function () { clearTimeout(timer); };
        }
    }, [focused]);

    return (
        <button
            ref={ref}
            onClick={function () {
                if (!isUpdating) {
                    onUpdate();
                }
            }}
            onMouseEnter={handleMouseEnter}
            className={"legacy-force-update-button" + (focused ? " is-focused" : "")}
            disabled={isUpdating}
        >
            {isUpdating ? "در حال بروزرسانی..." : "بروزرسانی"}
        </button>
    );
}

export default function ForceUpdateModal() {
    var { ref, focusKey } = useFocusable({
        focusKey: "force-update-modal",
        isFocusBoundary: true, // Trap focus within modal
        focusBoundaryDirections: ["up", "down", "left", "right"],
    });

    var [isUpdating, setIsUpdating] = React.useState(false);

    // Block back button and escape
    useEffect(function () {
        function handleKeyDown(e) {
            // Block Escape, Back, and browser back navigation
            if (
                e.keyCode === 27 || // Escape
                e.keyCode === 8 ||  // Backspace (browser back)
                e.keyCode === 461 || // LG WebOS Back
                e.keyCode === 10009 || // Samsung Tizen Back
                e.key === "GoBack" ||
                e.key === "BrowserBack"
            ) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        }

        // Block popstate (browser back)
        function handlePopState(e) {
            e.preventDefault();
            window.history.pushState(null, "", window.location.href);
        }

        window.addEventListener("keydown", handleKeyDown, true);
        window.addEventListener("popstate", handlePopState);
        window.history.pushState(null, "", window.location.href);

        return function () {
            window.removeEventListener("keydown", handleKeyDown, true);
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    function handleUpdate() {
        setIsUpdating(true);
        console.log("[ForceUpdateModal] Starting update...");

        // Force app update: clear cache, unregister SW, reload
        forceAppUpdate().catch(function (err) {
            console.error("[ForceUpdateModal] Update failed:", err);
            // Even if it fails, still reload
            window.location.reload(true);
        });
    }

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="legacy-force-update-overlay">
                <div className="legacy-force-update-card">
                    <div className="legacy-force-update-icon">🔄</div>
                    <h1 className="legacy-force-update-title">نسخه جدید موجود است</h1>
                    <p className="legacy-force-update-description">
                        نسخه فعلی برنامه قدیمی است و برای ادامه استفاده باید بروزرسانی کنید.
                    </p>
                    <UpdateButton onUpdate={handleUpdate} isUpdating={isUpdating} />
                    {isUpdating && (
                        <span className="legacy-force-update-loading">لطفاً صبر کنید...</span>
                    )}
                </div>
            </div>
        </FocusContext.Provider>
    );
}
